import { useEffect, useRef, useState } from "react";
import { ExportIeIssue } from "./ExportIeIssue";
import { FaPlus } from "react-icons/fa6";
import isGHMC from "../../../Common/helper/helpar";
import MUITable from "../../../Common/MUITable";
import { TableLoader } from "../../../Common/ShimmerLoader";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit } from "@fortawesome/free-solid-svg-icons";
import { StatusBadge } from "../../Rfid/utils/statusBadge";
import CreateIeIssueModal from "./CreateIeIssueModal";
import { useIeIssueStore } from "./store/useIeIssueStore";

const resetButtonStyle = {
  backgroundColor: "#FDE8E8",
  color: "#8B1A1A",
  border: "none",
  fontWeight: 600,
  padding: "8px 24px",
  borderRadius: "6px",
  fontSize: "14px",
};

const IeIssueRootComponent = () => {
  const isProd = isGHMC();
  const [showModal, setShowModal] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);

  const {
    filteredIeIssues,
    filterOriginalIeIssues,
    filters,
    pagination,
    kpiTypeOptions,
    isLoading,
    isSaving,
    fetchIeIssues,
    fetchKpiTypeOptions,
    fetchAllIeIssuesForExport,
    setSearchTerm,
    resetSearch,
    addIeIssue,
    updateIeIssue,
  } = useIeIssueStore();

  const isFirstSearchRender = useRef(true);
  const [exportData, setExportData] = useState([]);

  useEffect(() => {
    fetchIeIssues();
    fetchKpiTypeOptions();
  }, [fetchIeIssues, fetchKpiTypeOptions]);

  useEffect(() => {
    if (isFirstSearchRender.current) {
      isFirstSearchRender.current = false;
      return;
    }

    const timer = setTimeout(() => {
      fetchIeIssues({ currentPage: 1 });
    }, 400);

    return () => clearTimeout(timer);
  }, [filters.searchTerm, fetchIeIssues]);

  useEffect(() => {
    setExportData(filteredIeIssues);
  }, [filteredIeIssues]);

  const handleExportDataLoad = async () => {
    const data = await fetchAllIeIssuesForExport();
    setExportData(data);
    return data;
  };

  const handleAdd = () => {
    setIsEditMode(false);
    setEditRow(null);
    setShowModal(true);
  };

  const handleEdit = (row) => {
    setIsEditMode(true);
    setEditRow(row);
    setShowModal(true);
  };

  const handleResetSearch = () => {
    resetSearch();
  };

  const handlePageChange = (_event, newPage) => {
    fetchIeIssues({
      currentPage: newPage + 1,
    });
  };

  const handleRowsPerPageChange = (event) => {
    const nextPageSize = parseInt(event.target.value, 10);
    fetchIeIssues({
      currentPage: 1,
      pageSize: nextPageSize,
    });
  };

  const handleSave = async (payload, isEdit) => {
    if (isEdit) {
      return updateIeIssue(payload);
    }

    return addIeIssue(payload);
  };

  const ieIssueColumns = [
    {
      label: "KPI Type",
      render: (row) => row.kpiTypeName || "N/A",
    },
    {
      label: "Issue Type",
      render: (row) => row.issueType || "N/A",
    },
    {
      label: "Status",
      render: (row) => <StatusBadge isActive={row.isActive} compact />,
    },
    {
      label: "Actions",
      render: (row) => (
        <button
          type="button"
          className={`${isProd ? "edit-ghmc" : "edit-swm"}`}
          onClick={() => handleEdit(row)}
        >
          <FontAwesomeIcon icon={faEdit} />
        </button>
      ),
    },
  ];

  return (
    <>
      <div className="content text-start">
        <div className="page-content">
          <div className="d-flex justify-content-between align-items-start mt-3">
            <div>
              <h4 className="mb-1">IE Issue Type Information</h4>
            </div>
            <div className="d-flex align-items-center">
              <button
                type="button"
                className="button-container"
                onClick={handleAdd}
              >
                <FaPlus className="font-size-20 me-1" /> Add IE Issue Type
              </button>
              <ExportIeIssue
                ieIssueData={exportData}
                loadExportData={handleExportDataLoad}
              />
            </div>
          </div>

          <div className="row align-items-end mt-4">
            <div className="col-md-4">
              <div className="form-group d-flex gap-2">
                <input
                  type="text"
                  name="searchTerm"
                  value={filters.searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search Issue Type"
                  className="form-control bg-white"
                />
                <button
                  type="button"
                  style={resetButtonStyle}
                  onClick={handleResetSearch}
                >
                  RESET
                </button>
              </div>
            </div>
          </div>

          <div className="card shadow mt-3">
            <div className="card-body">
              <div>
                <MUITable
                  loading={isLoading}
                  needSRNumber={true}
                  rows={filteredIeIssues}
                  columns={ieIssueColumns}
                  serverPagination
                  totalCount={pagination.totalCount}
                  page={Math.max(0, pagination.currentPage - 1)}
                  rowsPerPage={pagination.pageSize}
                  onPageChange={handlePageChange}
                  onRowsPerPageChange={handleRowsPerPageChange}
                  rowsPerPageOptions={[5, 10, 25, 50]}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <CreateIeIssueModal
        showModal={showModal}
        setShowModal={setShowModal}
        editRow={editRow}
        isEditMode={isEditMode}
        existingIssues={filterOriginalIeIssues}
        kpiTypeOptions={kpiTypeOptions}
        onSave={handleSave}
        isSaving={isSaving}
      />
    </>
  );
};

export default IeIssueRootComponent;
